import { useEffect, useState } from "react";
import { Gift, Plus, Save, BarChart3, Trash2 } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, getErrorMessage } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const EMPTY_FORM = {
  name: "",
  description: "",
  code: "",
  voucher_type: "onboarding",
  trigger_event: "signup",
  reward_type: "bonus_credit",
  reward_currency: "INR",
  reward_amount: "",
  enabled: true,
  validity_days: 30,
  requires_kyc: false,
  min_trade_volume: 0,
  max_claims: "",
  max_claims_per_user: 1,
  block_duplicate_ip: true,
};

export default function RewardsBonuses() {
  const [meta, setMeta] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editMode, setEditMode] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const pager = useAdminPager();

  async function load() {
    try {
      const [m, v] = await Promise.all([
        api.get("/api/admin/rewards/meta"),
        api.get("/api/admin/rewards/vouchers", { params: { skip: pager.skip, limit: pager.limit } }),
      ]);
      setMeta(m.data);
      const { items, total } = unwrapAdminList(v.data);
      setVouchers(items);
      pager.setTotal(total);
      setErr("");
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pager.page]);

  async function loadStats(voucherId) {
    if (!voucherId) return;
    const { data } = await api.get(`/api/admin/rewards/vouchers/${voucherId}/stats`);
    setStats(data);
  }

  function selectVoucher(v) {
    setSelectedId(v.voucher_id);
    setEditMode(true);
    setForm({
      name: v.name || "",
      description: v.description || "",
      code: v.code || "",
      voucher_type: v.voucher_type || "onboarding",
      trigger_event: v.trigger_event || "signup",
      reward_type: v.reward_type || "bonus_credit",
      reward_currency: v.reward_currency || "INR",
      reward_amount: v.reward_amount || "",
      enabled: !!v.enabled,
      validity_days: v.validity_days ?? 30,
      requires_kyc: !!v.requires_kyc,
      min_trade_volume: Number(v.min_trade_volume || 0),
      max_claims: v.max_claims ?? "",
      max_claims_per_user: v.max_claims_per_user ?? 1,
      block_duplicate_ip: !!v.block_duplicate_ip,
    });
    loadStats(v.voucher_id);
  }

  function startCreate() {
    setSelectedId(null);
    setEditMode(false);
    setStats(null);
    setForm({ ...EMPTY_FORM, code: `PROMO_${Date.now().toString(36).toUpperCase().slice(-6)}` });
  }

  async function save() {
    setBusy(true);
    setMsg("");
    setErr("");
    const payload = {
      ...form,
      reward_amount: Number(form.reward_amount),
      validity_days: Number(form.validity_days),
      min_trade_volume: Number(form.min_trade_volume || 0),
      max_claims: form.max_claims === "" || form.max_claims == null ? null : Number(form.max_claims),
      max_claims_per_user: Number(form.max_claims_per_user || 1),
      code: String(form.code).toUpperCase().replace(/[^A-Z0-9_]/g, "_"),
    };
    try {
      if (editMode && selectedId) {
        const { data } = await api.patch(`/api/admin/rewards/vouchers/${selectedId}`, payload);
        setMsg(`Updated ${data.code}`);
      } else {
        const { data } = await api.post("/api/admin/rewards/vouchers", payload);
        setMsg(`Created ${data.code}`);
        setSelectedId(data.voucher_id);
        setEditMode(true);
      }
      await load();
      if (selectedId) loadStats(selectedId);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled(v) {
    await api.patch(`/api/admin/rewards/vouchers/${v.voucher_id}`, { enabled: !v.enabled });
    await load();
  }

  async function deleteVoucher() {
    if (!selectedId) return;
    const v = vouchers.find((x) => x.voucher_id === selectedId);
    const label = v?.code || selectedId;
    if (!window.confirm(`Delete voucher ${label}? Existing user grants stay in history.`)) return;
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      await api.delete(`/api/admin/rewards/vouchers/${selectedId}`);
      setMsg(`Deleted ${label}`);
      startCreate();
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminShell
      title="Marketing — Rewards & Bonuses"
      subtitle="Configure onboarding, KYC, and promotional vouchers. Rewards credit the bonus wallet only."
    >
      {err && <p className="text-[13px] text-red-400 mb-4">{err}</p>}
      {msg && <p className="text-[13px] text-emerald-400 mb-4">{msg}</p>}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel className="p-4 xl:col-span-1 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-semibold text-white flex items-center gap-2">
              <Gift size={16} /> Vouchers
            </h3>
            <Btn size="sm" onClick={startCreate} data-testid="reward-create-btn">
              <Plus size={14} /> New
            </Btn>
          </div>
          {vouchers.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No vouchers yet. Create one to enable onboarding rewards.</p>
          ) : (
            <ul className="space-y-2">
              {vouchers.map((v) => (
                <li key={v.voucher_id}>
                  <button
                    type="button"
                    onClick={() => selectVoucher(v)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      selectedId === v.voucher_id
                        ? "border-primary bg-background"
                        : "border-line hover:bg-background/60"
                    }`}
                  >
                    <div className="flex justify-between gap-2">
                      <span className="font-semibold text-[13px] text-white">{v.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${v.enabled ? "bg-emerald-900/40 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>
                        {v.enabled ? "ON" : "OFF"}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">{v.code} · {v.trigger_event}</p>
                    <p className="text-[11px] text-primary mt-0.5">
                      {v.reward_amount} {v.reward_currency} · {v.claims_count || 0} claims
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <AdminPager
            page={pager.page}
            total={pager.total}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
            testId="vouchers-pager"
          />
        </Panel>

        <Panel className="p-5 xl:col-span-2">
          <h3 className="text-[14px] font-semibold text-white mb-4">
            {editMode ? "Edit voucher" : "Create voucher"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px]">
            <label className="block md:col-span-2">
              <span className="text-muted-foreground text-[12px]">Name</span>
              <input className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="block md:col-span-2">
              <span className="text-muted-foreground text-[12px]">Description</span>
              <textarea className="mt-1 w-full min-h-[60px] px-3 py-2 bg-background border border-line rounded-md"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Code (unique)</span>
              <input className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md font-mono"
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={editMode} />
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Voucher type</span>
              <select className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.voucher_type} onChange={(e) => setForm({ ...form, voucher_type: e.target.value })}>
                {(meta?.voucher_types || ["onboarding", "kyc_bonus", "promotional", "manual"]).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Trigger (auto-assign)</span>
              <select className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.trigger_event} onChange={(e) => setForm({ ...form, trigger_event: e.target.value })}>
                {(meta?.trigger_events || ["signup", "kyc_approved", "manual"]).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Reward amount</span>
              <input type="number" step="0.01" className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.reward_amount} onChange={(e) => setForm({ ...form, reward_amount: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Currency</span>
              <input className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.reward_currency} onChange={(e) => setForm({ ...form, reward_currency: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Validity (days)</span>
              <input type="number" className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-muted-foreground text-[12px]">Max total claims (empty = unlimited)</span>
              <input type="number" className="mt-1 w-full h-[36px] px-3 bg-background border border-line rounded-md"
                value={form.max_claims} onChange={(e) => setForm({ ...form, max_claims: e.target.value })} placeholder="Unlimited" />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              <span className="text-muted-foreground">Enabled</span>
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.requires_kyc}
                onChange={(e) => setForm({ ...form, requires_kyc: e.target.checked })} />
              <span className="text-muted-foreground">Requires KYC before credit</span>
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.block_duplicate_ip}
                onChange={(e) => setForm({ ...form, block_duplicate_ip: e.target.checked })} />
              <span className="text-muted-foreground">Block duplicate signup IP</span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2 mt-6">
            <Btn onClick={save} disabled={busy} data-testid="reward-save-btn">
              <Save size={14} /> {busy ? "Saving…" : editMode ? "Save changes" : "Create voucher"}
            </Btn>
            {editMode && selectedId && (
              <Btn kind="ghost" onClick={() => {
                const v = vouchers.find((x) => x.voucher_id === selectedId);
                if (v) toggleEnabled(v);
              }}>
                Toggle enable/disable
              </Btn>
            )}
            {editMode && selectedId && (
              <Btn kind="danger" onClick={deleteVoucher} disabled={busy} data-testid="reward-delete-btn">
                <Trash2 size={14} /> Delete voucher
              </Btn>
            )}
          </div>

          {stats && (
            <div className="mt-6 p-4 rounded-md bg-background border border-line">
              <h4 className="text-[13px] font-semibold text-white flex items-center gap-2 mb-2">
                <BarChart3 size={14} /> Usage stats — {stats.code}
              </h4>
              <p className="text-[12px] text-muted-foreground">
                Total assignments: {stats.total_user_vouchers} · Claims counter: {stats.claims_count}
                {stats.max_claims != null ? ` / ${stats.max_claims}` : " (unlimited)"}
              </p>
              <ul className="mt-2 text-[12px] text-muted-foreground">
                {Object.entries(stats.by_status || {}).map(([k, n]) => (
                  <li key={k}>{k}: {n}</li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground max-w-3xl">
        No hardcoded bonuses: create vouchers with trigger <code className="text-primary">signup</code> for welcome
        rewards and <code className="text-primary">kyc_approved</code> for KYC bonuses. Credits go to the bonus wallet
        (spendable on trading, not withdrawable). Use <code className="text-primary">requires_kyc</code> to hold signup rewards until KYC passes.
      </p>
    </AdminShell>
  );
}
